import { currentWindowLabel, isTauriRuntime, MAIN_WINDOW_LABEL } from "src/lib/runtime/environment";

/**
 * The transport for keeping every native window's query cache honest.
 *
 * On the desktop a deal is spread across windows: the pipeline in the main one, a document
 * workspace in its own, a chat in another. Each is a separate webview with a separate React root
 * and a separate QueryClient, so an optimistic cache update — which is how nearly every mutation in
 * this app is reflected — is invisible to all the others. Retype a document in the workspace and
 * the deal's document list behind it still shows the old name, with nothing wrong on the server and
 * nothing for the reviewer to retry.
 *
 * This carries the RESULT of a cache write rather than a request to go and refetch it, so a
 * receiving window patches exactly what the acting window patched. The one exception is the
 * application graph — see `publishApplicationCacheChange`.
 *
 * Inert outside Tauri, where there is only ever one window.
 */

const CROSS_WINDOW_CACHE_EVENT = "cross-window-cache";

/**
 * Which window sent an event.
 *
 * Tauri's `emit` reaches every webview including the sender, so without this a window would replay
 * its own write — and, because replaying re-publishes, would do it forever. It has to be the native
 * window label: two windows that report the same id ignore each other's writes entirely, which is
 * what happened when the extra windows a cmd+clicked link opens all answered to "main".
 */
const senderId = (): string => currentWindowLabel() ?? MAIN_WINDOW_LABEL;

export type CrossWindowCacheChange =
  | {
      kind: "document";
      applicationId: string;
      documentId: string;
      /** The document as the acting window left it, serialized. */
      document: unknown;
    }
  | { kind: "document-removed"; applicationId: string; documentId: string }
  /**
   * The deal's document list should be read again, rather than patched.
   *
   * For changes that alter which documents EXIST — a split, a re-split retiring the previous
   * generation, a source retired into an internal pre-split document. The server decides what
   * belongs in that list (it hides deleted files and internal ones), so replaying individual
   * patches into another window can only approximate it, and does not approximate it at all when
   * the acting window did not know the full set it had changed.
   */
  | { kind: "documents"; applicationId: string }
  /**
   * The deal's email thread list should be read again.
   *
   * Sending from a detached compose window prepends the new Communication to that window's own
   * `["mail", …]` pages and nothing else — so the deal's Comms tab in the main window would keep
   * showing the thread without the reply that was just sent. Re-read rather than patch: the list is
   * paginated and thread-grouped server-side, and the sending window only knows its own page 0.
   */
  | { kind: "communications"; applicationId: string }
  /**
   * The deal's proposed communications should be read again.
   *
   * Sending an LLM-drafted email resolves its proposal, which is what removes it from the
   * proposed-communications card and stops it being offered again. The window that sent is usually
   * NOT the window showing that card.
   */
  | { kind: "proposed-communications"; applicationId: string }
  /**
   * Only an id. The application graph is the largest object this app holds and the most frequently
   * patched — every optimistic field edit touches it — so shipping it between windows on each
   * keystroke would cost far more than re-reading it once. The receiver invalidates instead.
   */
  | { kind: "application"; applicationId: string };

type Published = CrossWindowCacheChange & { senderId: string };

/**
 * True while a received change is being applied.
 *
 * The cache writers publish, and applying a received change goes through those same writers — so
 * without this the two windows would hand one change back and forth indefinitely. It wraps the
 * APPLYING rather than the receiving, so anything that applies a change is covered whether or not
 * it arrived through the subscription.
 */
let applying = false;

export function applyingCrossWindowCacheChange(): boolean {
  return applying;
}

export function withCrossWindowCacheChangeApplied(apply: () => void): void {
  applying = true;
  try {
    apply();
  } finally {
    applying = false;
  }
}

export function publishCrossWindowCacheChange(change: CrossWindowCacheChange): void {
  if (!isTauriRuntime() || applying) return;
  void (async () => {
    const { emit } = await import("@tauri-apps/api/event");
    await emit(CROSS_WINDOW_CACHE_EVENT, { ...change, senderId: senderId() } satisfies Published);
  })().catch((error) => console.warn("Could not publish a cache change to other windows.", error));
}

/**
 * How long application-graph changes are collected before one is sent.
 *
 * The window doing the editing has already patched its own cache, so nothing it shows is waiting on
 * this. What is waiting is the OTHER window's refetch of the deal, and a burst of field edits should
 * cost one of those rather than one each.
 */
const APPLICATION_CHANGE_DEBOUNCE_MS = 1_500;
const pendingApplicationChanges = new Map<string, ReturnType<typeof setTimeout>>();

export function publishApplicationCacheChange(applicationId: string): void {
  if (!isTauriRuntime() || applying || !applicationId) return;
  const existing = pendingApplicationChanges.get(applicationId);
  if (existing) clearTimeout(existing);
  pendingApplicationChanges.set(
    applicationId,
    setTimeout(() => {
      pendingApplicationChanges.delete(applicationId);
      publishCrossWindowCacheChange({ kind: "application", applicationId });
    }, APPLICATION_CHANGE_DEBOUNCE_MS)
  );
}

/** Listen for changes made in the other windows. Returns a no-op unsubscribe outside Tauri. */
export async function subscribeToCrossWindowCacheChanges(
  onChange: (change: CrossWindowCacheChange) => void
): Promise<() => void> {
  if (!isTauriRuntime()) return () => undefined;
  const { listen } = await import("@tauri-apps/api/event");
  const self = senderId();
  return listen<Published>(CROSS_WINDOW_CACHE_EVENT, ({ payload }) => {
    if (payload.senderId === self) return;
    onChange(payload);
  });
}
